/**
 * Google Analytics 4 Helper - Utilitários para tracking e eventos
 */

import React from 'react'

// Configuração do GA4
const GA_CONFIG = {
  measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX',
  debug: import.meta.env.DEV === true,
  cookieFlags: 'SameSite=None;Secure'
}

/**
 * Inicializa o Google Analytics 4
 */
export const initGA4 = () => {
  if (typeof window === 'undefined' || !GA_CONFIG.measurementId || GA_CONFIG.measurementId === 'G-XXXXXXXXXX') {
    if (GA_CONFIG.debug) console.log('GA4: Analytics não inicializado - ID de medição não configurado')
    return
  }

  // Carregar script do GA4
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_CONFIG.measurementId}`
  document.head.appendChild(script)

  // Inicializar gtag
  window.dataLayer = window.dataLayer || []
  function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag = gtag

  gtag('js', new Date())
  gtag('config', GA_CONFIG.measurementId, {
    debug_mode: GA_CONFIG.debug,
    send_page_view: false // Vamos controlar manualmente
  })

  if (GA_CONFIG.debug) console.log('GA4: Analytics inicializado com ID:', GA_CONFIG.measurementId)
}

/**
 * Envia evento de visualização de página
 * @param {string} title - Título da página
 * @param {string} path - Caminho da página
 */
export const trackPageView = (title, path = window.location.pathname) => {
  if (typeof window.gtag === 'undefined') {
    if (GA_CONFIG.debug) console.warn('GA4: gtag não disponível para pageview')
    return
  }

  const eventData = {
    page_title: title,
    page_location: window.location.href,
    page_path: path
  }

  window.gtag('config', GA_CONFIG.measurementId, eventData)
  
  if (GA_CONFIG.debug) {
    console.log('GA4: Page view tracked:', eventData)
  }
}

/**
 * Envia evento personalizado
 * @param {string} eventName - Nome do evento
 * @param {Object} parameters - Parâmetros do evento
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...parameters })
  }

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, parameters)
  }

  if (GA_CONFIG.debug) {
    console.log('GA4: Event tracked:', eventName, parameters)
  }
}

/**
 * Eventos predefinidos para o site institucional
 */
export const EVENTS = {
  // Navegação
  PAGE_VIEW: 'page_view',
  
  // CTAs principais
  CTA_CLICK: 'cta_click',
  FREE_PLAN_START: 'free_plan_start',
  DEMO_REQUEST: 'demo_request',
  
  // Formulários
  FORM_START: 'form_start',
  FORM_SUBMIT: 'form_submit',
  FORM_ERROR: 'form_error',
  
  // Engajamento
  VIDEO_PLAY: 'video_play',
  FILE_DOWNLOAD: 'file_download',
  EXTERNAL_LINK: 'click',
  
  // Calculadoras
  CALCULATOR_USE: 'calculator_use',
  CALCULATOR_RESULT: 'calculator_result',
  
  // Conversões
  LEAD_GENERATION: 'generate_lead',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  CONTACT_ATTEMPT: 'contact_attempt'
}

/**
 * Funções de tracking específicas
 */

// CTA e botões principais
export const trackCTAClick = (ctaName, location, value = null) => {
  trackEvent(EVENTS.CTA_CLICK, {
    cta_name: ctaName,
    cta_location: location,
    value,
    currency: 'BRL'
  })
}

// Solicitação de demo
export const trackDemoRequest = (planType = null, userInfo = {}) => {
  trackEvent(EVENTS.DEMO_REQUEST, {
    event_category: 'Lead Generation',
    event_label: planType,
    custom_parameters: {
      plan_interest: planType,
      company_size: userInfo.companySize,
      industry: userInfo.industry
    }
  })
}

// Início do plano gratuito
export const trackFreePlanStart = (planName, planValue) => {
  trackEvent(EVENTS.FREE_PLAN_START, {
    event_category: 'Conversion',
    currency: 'BRL',
    value: planValue,
    items: [{
      item_id: planName.toLowerCase().replace(' ', '_'),
      item_name: planName,
      item_category: 'Subscription Plan',
      price: planValue,
      quantity: 1
    }]
  })
}

// Formulários
export const trackFormStart = (formName, formLocation) => {
  trackEvent(EVENTS.FORM_START, {
    form_name: formName,
    form_location: formLocation
  })
}

export const trackFormSubmit = (formName, formLocation, success = true) => {
  const eventName = success ? EVENTS.FORM_SUBMIT : EVENTS.FORM_ERROR
  trackEvent(eventName, {
    form_name: formName,
    form_location: formLocation,
    success
  })
}

// Calculadoras
export const trackCalculatorUse = (calculatorType, inputs = {}) => {
  trackEvent(EVENTS.CALCULATOR_USE, {
    calculator_type: calculatorType,
    event_category: 'Engagement',
    custom_parameters: inputs
  })
}

export const trackCalculatorResult = (calculatorType, result = {}) => {
  trackEvent(EVENTS.CALCULATOR_RESULT, {
    calculator_type: calculatorType,
    event_category: 'Engagement',
    custom_parameters: result
  })
}

// Newsletter
export const trackNewsletterSignup = (location, email = null) => {
  trackEvent(EVENTS.NEWSLETTER_SIGNUP, {
    event_category: 'Lead Generation',
    signup_location: location,
    custom_parameters: {
      email_domain: email ? email.split('@')[1] : null
    }
  })
}

// Links externos
export const trackExternalLink = (url, linkText) => {
  trackEvent(EVENTS.EXTERNAL_LINK, {
    link_domain: new URL(url).hostname,
    link_text: linkText,
    link_url: url,
    outbound: true
  })
}

// Downloads
export const trackFileDownload = (fileName, fileType, fileUrl) => {
  trackEvent(EVENTS.FILE_DOWNLOAD, {
    file_name: fileName,
    file_extension: fileType,
    file_url: fileUrl,
    event_category: 'Engagement'
  })
}

// Vídeos
export const trackVideoPlay = (videoTitle, videoUrl, videoDuration = null) => {
  trackEvent(EVENTS.VIDEO_PLAY, {
    video_title: videoTitle,
    video_url: videoUrl,
    video_duration: videoDuration,
    event_category: 'Engagement'
  })
}

// Contato via WhatsApp
export const trackWhatsAppContact = (message, location) => {
  trackEvent(EVENTS.CONTACT_ATTEMPT, {
    contact_method: 'whatsapp',
    contact_location: location,
    event_category: 'Lead Generation'
  })
}

/**
 * Hook React para analytics
 */
export const useAnalytics = () => {
  const trackPageView = (title, path) => {
    window.requestIdleCallback?.(() => {
      trackPageView(title, path)
    }) || setTimeout(() => trackPageView(title, path), 0)
  }

  const trackEvent = (eventName, parameters) => {
    window.requestIdleCallback?.(() => {
      trackEvent(eventName, parameters)
    }) || setTimeout(() => trackEvent(eventName, parameters), 0)
  }

  return {
    trackPageView,
    trackEvent,
    trackCTAClick,
    trackDemoRequest,
    trackFreePlanStart,
    trackFormStart,
    trackFormSubmit,
    trackCalculatorUse,
    trackCalculatorResult,
    trackNewsletterSignup,
    trackExternalLink,
    trackFileDownload,
    trackVideoPlay,
    trackWhatsAppContact
  }
}

/**
 * Componente React para inicializar Analytics
 */
export const AnalyticsProvider = ({ children }) => {
  React.useEffect(() => {
    initGA4()
  }, [])

  return children
}

/**
 * Enhanced ecommerce para tracking de planos
 */
export const trackPlanView = (plan) => {
  trackEvent('view_item', {
    currency: 'BRL',
    value: plan.price,
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: 'Subscription Plan',
      item_variant: plan.billing || 'monthly',
      price: plan.price,
      quantity: 1
    }]
  })
}

export const trackPlanSelect = (plan) => {
  trackEvent('select_item', {
    item_list_name: 'Pricing Plans',
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: 'Subscription Plan',
      price: plan.price
    }]
  })
}

/**
 * Configuração de conversões personalizadas
 */
export const trackCustomConversion = (conversionName, value = null, currency = 'BRL') => {
  trackEvent('conversion', {
    conversion_name: conversionName,
    value,
    currency,
    event_category: 'Custom Conversion'
  })
}

/**
 * Utilidades para debugging
 */
export const enableDebug = () => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_CONFIG.measurementId, {
      debug_mode: true
    })
  }
}

export const getAnalyticsStatus = () => {
  return {
    initialized: typeof window.gtag !== 'undefined',
    measurementId: GA_CONFIG.measurementId,
    debug: GA_CONFIG.debug,
    dataLayer: window.dataLayer?.length || 0
  }
}

export default {
  initGA4,
  trackPageView,
  trackEvent,
  trackCTAClick,
  trackDemoRequest,
  trackFreePlanStart,
  trackFormStart,
  trackFormSubmit,
  trackCalculatorUse,
  trackCalculatorResult,
  trackNewsletterSignup,
  trackExternalLink,
  trackFileDownload,
  trackVideoPlay,
  trackWhatsAppContact,
  trackPlanView,
  trackPlanSelect,
  trackCustomConversion,
  useAnalytics,
  AnalyticsProvider,
  enableDebug,
  getAnalyticsStatus,
  EVENTS
}
